/**
 * 표 DOM 렌더/싱크 (P0-⑤a / P3) — 표 모델 → 라이브 `<table>` DOM, 라이브 편집 → 모델.content.
 * 샘플 renderTable/syncFromDOM 패턴(D5). 셀은 contenteditable.
 *  - renderTableElement(model): 새 <table> 생성
 *  - rerenderTable(table): 기존 <table>을 제자리 재구축(구조 연산 후)
 *  - syncModelFromTableDom(table): 셀 편집 내용을 모델로 회수
 */

import { inlineContentToHtml, htmlToInlineContent } from "../serialize/inline.js";

/** colgroup + tbody를 table 안에 (재)구축 */
function buildTable(table, model) {
  while (table.firstChild) table.removeChild(table.firstChild);
  const G = model.G || [];
  const rows = G.length;
  const cols = rows && G[0] ? G[0].length : 0;

  const colgroup = document.createElement("colgroup");
  for (let c = 0; c < cols; c++) {
    const col = document.createElement("col");
    const w = model.columnWidths && model.columnWidths[c];
    if (w) col.style.width = w + "px";
    colgroup.appendChild(col);
  }
  table.appendChild(colgroup);

  const tbody = document.createElement("tbody");
  for (let r = 0; r < rows; r++) {
    const tr = document.createElement("tr");
    for (let c = 0; c < cols; c++) {
      const m = G[r][c];
      if (!m || m.r !== r || m.c !== c) continue; // master 좌상단에서만
      const td = document.createElement("td");
      td.className = "lumir-cell";
      td.setAttribute("contenteditable", "true");
      // 접근성: 머리행/열 셀에 ARIA 역할 부여(태그는 <td> 유지 — 셀 선택자/편집 흐름 불변).
      if ((model.headerRows || 0) > m.r) td.setAttribute("role", "columnheader");
      else if ((model.headerCols || 0) > m.c) td.setAttribute("role", "rowheader");
      if (m.colspan > 1) td.colSpan = m.colspan;
      if (m.rowspan > 1) td.rowSpan = m.rowspan;
      if (m.textAlignment) td.style.textAlign = m.textAlignment;
      if (m.verticalAlignment) td.style.verticalAlign = m.verticalAlignment;
      if (m.backgroundColor) td.dataset.backgroundColor = m.backgroundColor;
      if (m.textColor) td.dataset.textColor = m.textColor;
      if (m.rowHeight) {
        td.dataset.rowHeight = String(m.rowHeight);
        tr.style.height = m.rowHeight + "px";
      }
      td.dataset.r = String(m.r);
      td.dataset.c = String(m.c);
      td.__master = m;
      td.innerHTML = inlineContentToHtml(m.content);
      tr.appendChild(td);
    }
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);

  // 머리행/열 스타일 클래스(CSS가 첫 행/열 강조)
  table.classList.toggle("hr", (model.headerRows || 0) > 0);
  table.classList.toggle("hc", (model.headerCols || 0) > 0);
  // compact: 열이 많아 열폭이 기본최소(40=CSS td min-width) 미만으로 눌린 상태 → CSS min-width를 낮춤(모델=렌더 정합).
  table.classList.toggle("lumir-tbl--compact", (model.columnWidths || []).some((w) => w && w < 40));
}

/** 표 모델 → 새 `<table class="lumir-tbl">` */
export function renderTableElement(model) {
  if (typeof document === "undefined") throw new Error("renderTableElement: DOM(document) 필요");
  const table = document.createElement("table");
  table.className = "lumir-tbl";
  table.__model = model;
  buildTable(table, model);
  return table;
}

/** 기존 `<table>`을 모델로 제자리 재구축(구조 연산 후 호출) */
export function rerenderTable(table) {
  if (!table || !table.__model) return;
  buildTable(table, table.__model);
}

/** 라이브 `<table>` 셀 편집 내용을 모델 master.content로 회수(샘플 syncFromDOM) */
export function syncModelFromTableDom(tableEl) {
  if (!tableEl || !tableEl.querySelectorAll) return;
  tableEl.querySelectorAll("td.lumir-cell").forEach((td) => {
    if (td.__master) td.__master.content = htmlToInlineContent(td);
  });
}
